import { Bell } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/cargofl-ui';
import { useMarkNotificationsRead, useNotifications } from '@/lib/engagement-api';
import { cn } from '@/lib/utils';

export function NotificationBell() {
  const { data: notifications } = useNotifications();
  const markRead = useMarkNotificationsRead();

  const items = notifications ?? [];
  const unread = items.filter((n) => n.readAt === null);

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        // Marked read on open, not on click: the user has seen them, and leaving a
        // badge up after they have been read is how people stop trusting the badge.
        if (open && unread.length > 0) markRead.mutate(undefined);
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label={
          unread.length > 0 ? `${unread.length} unread notifications` : 'Notifications'
        }>
          <Bell className="nav-icon size-4" data-motion="ring" />
          {unread.length > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex size-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
              {unread.length > 9 ? '9+' : unread.length}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>Notifications</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {items.length === 0 && (
          <div className="px-2 py-6 text-center text-sm text-muted-foreground">
            Nothing yet.
          </div>
        )}

        {items.slice(0, 12).map((n) => {
          const content = (
            <div className="flex flex-col gap-0.5">
              <span className={cn('text-sm', n.readAt === null && 'font-semibold')}>{n.title}</span>
              {n.body && (
                <span className="line-clamp-2 text-xs text-muted-foreground">{n.body}</span>
              )}
              <span className="text-[11px] text-muted-foreground">
                {new Date(n.createdAt).toLocaleDateString()}
              </span>
            </div>
          );

          return (
            <DropdownMenuItem key={n.id} asChild={Boolean(n.linkPath)} className="items-start">
              {n.linkPath ? <Link to={n.linkPath}>{content}</Link> : content}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
